import {
  BoolInputValue as BoolInputValueProto,
  DateInputValue as DateInputValueProto,
  DocumentsInputValue as DocumentsInputValueProto,
  InputValue as InputValueProto,
  NumericInputValue as NumericInputValueProto,
  type Question as QuestionProto,
  SelectInputValue as SelectInputValueProto,
  TextInputValue as TextInputValueProto,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import type { Map as MapProto } from 'google-protobuf'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { numberToDecimal, safeDecimalToNumber } from '@/utils/grpc-util'
import { maybeParseDate } from '@/utils/date-util'
import type { QuestionResource } from '@/features/jobs/api/resources/questions'
import type {
  AnswersMap,
  BoolAnswer,
  DateTimeAnswer,
  DocumentsAnswer,
  MultiSelectAnswer,
  NumericAnswer,
  Question,
  QuestionAnswer,
  SingleSelectAnswer,
  TextAnswer,
} from '@/features/jobs/questions/questions'

/**
 * Maps a question proto to the local type.
 *
 * @param proto
 */
export function mapQuestionProtoToQuestion(proto: QuestionProto): Question {
  const inputValue = proto.getAnswer()
  const props = {
    id: proto.getId(),
    title: proto.getQuestion(),
    description: proto.getDescription()?.getValue(),
  }

  switch (proto.getType()) {
    case 'Bool': {
      return {
        ...props,
        type: 'Bool',
        answer: mapInputValueProtoToBoolAnswer(inputValue),
      }
    }
    case 'Text': {
      return {
        ...props,
        type: 'Text',
        answer: mapInputValueProtoToTextAnswer(inputValue),
      }
    }
    case 'Numeric': {
      const format = proto.getFormat()
      return {
        ...props,
        type: 'Numeric',
        format:
          format?.getType() === 'Integer'
            ? { type: 'Integer' }
            : { type: 'Decimal', places: format?.getPlaces() ?? 0 },
        answer: mapInputValueProtoToNumericAnswer(inputValue),
      }
    }
    case 'DateTime': {
      return {
        ...props,
        type: 'DateTime',
        collectTime: proto.getCollectTime(),
        disallowPast: proto.getDisallowPast(),
        disallowFuture: proto.getDisallowFuture(),
        answer: mapInputValueProtoToDateTimeAnswer(inputValue),
      }
    }
    case 'Select': {
      const multi = proto.getMulti()?.getValue() ?? false
      return {
        ...props,
        type: 'Select',
        multi,
        options: proto.getOptionsList().map((option) => ({
          id: option.getId(),
          text: option.getText(),
        })),
        answer: mapInputValueProtoToSelectAnswer(inputValue, multi),
      }
    }
    case 'Documents': {
      return {
        ...props,
        type: 'Documents',
        options: proto.getOptionsList().map((option) => ({
          id: option.getId(),
          text: option.getText(),
        })),
        answer: mapInputValueProtoToDocumentsAnswer(inputValue),
      }
    }
    case 'Breaker': {
      return {
        ...props,
        type: 'Breaker',
        answer: null,
      }
    }
    default: {
      return {
        ...props,
        type: 'Unknown',
        questionType: proto.getType(),
        answer: null,
      }
    }
  }
}

/**
 * Maps a question resource, such as a manual input of a scope work template,
 * to the local type.
 */
export function mapQuestionResourceToQuestion(
  resource: QuestionResource,
): Question {
  const props = {
    id: resource.id,
    title: resource.question ?? '',
    description: resource.description,
  }
  const answer: unknown = resource.answer

  switch (resource.type) {
    case 'BOOL':
      return {
        ...props,
        type: 'Bool',
        answer:
          typeof answer === 'boolean' ? { type: 'Bool', value: answer } : null,
      }
    case 'SELECT':
      return {
        ...props,
        type: 'Select',
        multi: !!resource.multi,
        options: resource.options.map((o) => ({ id: o.id, text: o.text })),
        answer: Array.isArray(answer)
          ? { type: 'MultiSelectAnswer', value: answer as string[] }
          : typeof answer === 'string'
            ? { type: 'SingleSelectAnswer', value: answer }
            : null,
      }
    case 'TEXT':
      return {
        ...props,
        type: 'Text',
        answer:
          typeof answer === 'string' ? { type: 'Text', value: answer } : null,
      }
    case 'NUMERIC':
      return {
        ...props,
        type: 'Numeric',
        format:
          resource.format?.type === 'Decimal'
            ? { type: 'Decimal', places: resource.format.places ?? 0 }
            : { type: 'Integer' },
        answer:
          typeof answer === 'number'
            ? { type: 'Numeric', value: answer }
            : null,
      }
    case 'DATE_TIME': {
      const value =
        answer instanceof Date
          ? answer
          : typeof answer === 'string'
            ? maybeParseDate(answer)
            : undefined
      return {
        ...props,
        type: 'DateTime',
        collectTime: !!resource.collect_time,
        disallowPast: !!resource.disallow_past,
        disallowFuture: !!resource.disallow_future,
        answer: value ? { type: 'DateTime', value } : null,
      }
    }
    case 'DOCUMENTS':
      return {
        ...props,
        type: 'Documents',
        options: resource.options.map((o) => ({
          id: o.id,
          text: o.text,
          documentTagIds: o.document_tags,
        })),
        answer: resource.answer
          ? {
              type: 'Documents',
              value: resource.answer.map((a) => ({
                id: a.id,
                documentId: a.document_id,
                supplemental: a.supplemental,
                title: a.title,
              })),
            }
          : null,
      }
    case 'BREAKER':
      return { ...props, type: 'Breaker', answer: null }
  }
}

/**
 * Maps an input value proto to a {@link BoolAnswer}.
 *
 * @param proto
 */
function mapInputValueProtoToBoolAnswer(
  proto: InputValueProto | undefined,
): BoolAnswer | null {
  const value = proto?.getBoolValue()
  return value ? { type: 'Bool', value: value.getValue() } : null
}

/**
 * Maps an input value proto to a {@link TextAnswer}.
 *
 * @param proto
 */
function mapInputValueProtoToTextAnswer(
  proto: InputValueProto | undefined,
): TextAnswer | null {
  const value = proto?.getTextValue()
  return value ? { type: 'Text', value: value.getValue() } : null
}

/**
 * Maps an input value proto to a {@link NumericAnswer}. Numeric questions
 * always carry an answer; a missing value reads as zero.
 *
 * @param proto
 */
function mapInputValueProtoToNumericAnswer(
  proto: InputValueProto | undefined,
): NumericAnswer {
  return {
    type: 'Numeric',
    value: safeDecimalToNumber(proto?.getNumericValue()?.getValue()),
  }
}

/**
 * Maps an input value proto to a {@link DateTimeAnswer}.
 *
 * @param proto
 */
function mapInputValueProtoToDateTimeAnswer(
  proto: InputValueProto | undefined,
): DateTimeAnswer | null {
  const value = proto?.getDateValue()?.getValue()
  return value ? { type: 'DateTime', value: value.toDate() } : null
}

/**
 * Maps an input value proto to a select answer.
 *
 * @param proto
 * @param multi Whether the question allows selecting several options.
 */
function mapInputValueProtoToSelectAnswer(
  proto: InputValueProto | undefined,
  multi: boolean,
): SingleSelectAnswer | MultiSelectAnswer | null {
  const selection = proto?.getSelectValue()
  if (multi) {
    const selectedIds = selection?.getMulti()?.getSelectedIdsList()
    return selectedIds
      ? { type: 'MultiSelectAnswer', value: selectedIds }
      : null
  }

  const single = selection?.getSingle()
  return single
    ? { type: 'SingleSelectAnswer', value: single.getSelectedId() }
    : null
}

/**
 * Maps an input value proto to a {@link DocumentsAnswer}.
 *
 * @param proto
 */
function mapInputValueProtoToDocumentsAnswer(
  proto: InputValueProto | undefined,
): DocumentsAnswer | null {
  const documents = proto?.getDocumentsValue()
  if (!documents) {
    return null
  }

  return {
    type: 'Documents',
    value: documents.getSelectedDocumentsList().map((document) => ({
      id: document.getId(),
      documentId: document.getLinkedDocumentId(),
    })),
  }
}

/**
 * Populates (mutates!) the given map proto with the answer map by mapping
 * the answers to the proper proto.
 *
 * @param source
 * @param destProto
 */
export function populateAnswerMapProto(
  source: AnswersMap,
  destProto: MapProto<string, InputValueProto>,
) {
  for (const [id, answer] of source.entries()) {
    destProto.set(id, mapQuestionAnswerToInputValueProto(answer))
  }
}

/**
 * Maps a {@link QuestionAnswer} to an input value proto.
 *
 * @param answer
 */
export function mapQuestionAnswerToInputValueProto(
  answer: QuestionAnswer,
): InputValueProto {
  const proto = new InputValueProto()
  switch (answer.type) {
    case 'Bool':
      proto.setBoolValue(new BoolInputValueProto().setValue(answer.value))
      break
    case 'SingleSelectAnswer':
      proto.setSelectValue(
        new SelectInputValueProto().setSingle(
          new SelectInputValueProto.Single().setSelectedId(answer.value),
        ),
      )
      break
    case 'MultiSelectAnswer':
      proto.setSelectValue(
        new SelectInputValueProto().setMulti(
          new SelectInputValueProto.Multi().setSelectedIdsList(answer.value),
        ),
      )
      break
    case 'Text':
      proto.setTextValue(new TextInputValueProto().setValue(answer.value))
      break
    case 'Numeric':
      proto.setNumericValue(
        new NumericInputValueProto().setValue(numberToDecimal(answer.value)),
      )
      break
    case 'DateTime': {
      const value = new Timestamp()
      value.fromDate(answer.value)
      proto.setDateValue(new DateInputValueProto().setValue(value))
      break
    }
    case 'Documents':
      proto.setDocumentsValue(
        new DocumentsInputValueProto().setSelectedDocumentsList(
          answer.value.map((entry) =>
            new DocumentsInputValueProto.DocumentsAnswerEntry()
              .setId(entry.id)
              .setLinkedDocumentId(entry.documentId),
          ),
        ),
      )
      break
    case 'Unknown':
      console.warn(new Error(`Unknown answer type: ${answer.type}`))
      break
  }
  return proto
}
